import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Chip,
  Stack,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CustomModal from "../../Comman/Modal";
import moment from "moment/moment";
import TableCompo from "../Table/TableCompo";
import { RiDeleteBin6Line } from "react-icons/ri";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { canShowPermission } from "../Permissions/permissionObject";
import CreateMuhurt from "./CreateMuhurt";

const gradientBtn = {
  textTransform: "none",
  borderRadius: "10px",
  background: "linear-gradient(135deg, #752ADA 0%, #9b4dff 100%)",
  boxShadow: "0 4px 14px rgba(117, 42, 218, 0.28)",
  "&:hover": {
    background: "linear-gradient(135deg, #5f1fb8 0%, #7a3dd4 100%)",
  },
};

export default function Muhurt() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [notificationList, setNotificationList] = useState([]);
  const [open, setOpen] = useState(false);
  const [update, setUpdate] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(3, "month"));
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);

  const canCreateMuhurt = canShowPermission(permissions, "muhurt", "create");
  const canDeleteMuhurt = canShowPermission(permissions, "muhurt", "delete");

  async function getMuhurt() {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    if (startDate.isAfter(endDate)) {
      alert("Start date cannot be after end date");
      return;
    }

    setLoading(true);
    try {
      const startDateEpoch = startDate.startOf("day").valueOf();
      const endDateEpoch = endDate.endOf("day").valueOf();

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/muhurt`,
        {
          params: {
            startDate: startDateEpoch,
            endDate: endDateEpoch,
            page: page + 1,
            limit: limit,
            search: search,
          },
        },
      );

      if (response.data.success) {
        if (response.data.data && response.data.data.muhurts.length) {
          setNotificationList(response.data.data.muhurts);
          setTotal(response.data.data.count || 0);
        } else {
          setNotificationList([]);
          setTotal(0);
        }
      } else {
        alert(response.data.message || "Failed to fetch Muhurt data");
        setNotificationList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching Muhurt:", error);
      alert(error.response?.data?.message || "Failed to fetch Muhurt data");
      setNotificationList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getMuhurt();
  }, [open]);

  useEffect(() => {
    if (selectedRow) {
      setUpdate(true);
    }
  }, [selectedRow]);

  async function handleDelete(id) {
    let confirm = window.confirm("Are you sure you want to delete?");
    if (confirm) {
      try {
        const response = await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/muhurt/${id}`,
        );

        if (response.data.success) {
          alert(response.data.message || "Muhurt deleted successfully");
          await getMuhurt();
        } else {
          alert(response.data.message || "Failed to delete Muhurt");
        }
      } catch (error) {
        console.error("Error deleting Muhurt:", error);
        alert(error.response?.data?.message || "Failed to delete Muhurt");
      }
    }
  }

  const handleTableChange = (type, payload) => {
    if (type === "changePage") {
      setPage(payload.page);
    }

    if (type === "changeRowsPerPage") {
      setLimit(payload.rowPerPage);
      setPage(0);
    }

    if (type === "search") {
      setSearch(payload.searchText || "");
      setPage(0);
    }
  };

  useEffect(() => {
    getMuhurt();
  }, [page, limit, search]);

  const columns = [
    {
      field: "title",
      headerName: "Title",
      width: isMobile ? 140 : 200,
      render: (value, row) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            maxWidth: isMobile ? 140 : 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.title || "N/A"}
        </Typography>
      ),
    },
    {
      field: "body",
      headerName: "Content",
      width: isMobile ? 160 : 260,
      render: (value, row) => (
        <Box
          sx={{
            maxWidth: isMobile ? 160 : 260,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "text.secondary",
          }}
        >
          {row.body || "N/A"}
        </Box>
      ),
    },
    {
      field: "url",
      headerName: "Image",
      width: 120,
      render: (value, row) =>
        row.url ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ImageOutlinedIcon fontSize="small" />}
            onClick={() => window.open(row.url, "_blank")}
            sx={{
              textTransform: "none",
              borderColor: "#c9a8ff",
              color: "#752ADA",
              borderRadius: "8px",
              whiteSpace: "nowrap",
            }}
          >
            View
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      field: "muhurtStart",
      headerName: "Start",
      width: isMobile ? 150 : 180,
      render: (value, row) => (
        <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
          {row.muhurtStart
            ? moment(row.muhurtStart).format("DD-MM-YYYY hh:mm A")
            : "N/A"}
        </Typography>
      ),
    },
    {
      field: "muhurtEnd",
      headerName: "End",
      width: isMobile ? 150 : 180,
      render: (value, row) => (
        <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
          {row.muhurtEnd
            ? moment(row.muhurtEnd).format("DD-MM-YYYY hh:mm A")
            : "N/A"}
        </Typography>
      ),
    },
    {
      field: "delete",
      headerName: "Delete",
      width: 90,
      render: (value, row) => (
        <Box>
          {canDeleteMuhurt ? (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(244, 67, 54, 0.08)",
                cursor: "pointer",
                transition: "background 0.2s",
                "&:hover": { bgcolor: "rgba(244, 67, 54, 0.16)" },
              }}
              onClick={() => handleDelete(row._id)}
            >
              <RiDeleteBin6Line style={{ color: "#e53935", fontSize: "18px" }} />
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              N/A
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: "16px",
          border: "1px solid #ece6f8",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden",
          background: "#fff",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            borderBottom: "1px solid #ece6f8",
            background: "linear-gradient(180deg, #faf8ff 0%, #fff 100%)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #752ADA 0%, #9b4dff 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <EventAvailableOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography
                  fontWeight={700}
                  fontSize={{ xs: 18, sm: 22 }}
                  fontFamily="Aeonik"
                  color="#1a1a2e"
                >
                  Muhurt
                </Typography>
                <Chip
                  label={`${total} entr${total === 1 ? "y" : "ies"}`}
                  size="small"
                  sx={{ bgcolor: "#f2eaff", color: "#7A3FFF", fontWeight: 600 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Manage auspicious dates & timings
              </Typography>
            </Box>
          </Stack>

          {canCreateMuhurt && (
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => setOpen(true)}
              fullWidth={isMobile}
              sx={{ ...gradientBtn, width: { xs: "100%", sm: "auto" } }}
            >
              Create Muhurt
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: "12px",
                bgcolor: "#faf8ff",
                border: "1px solid #ece6f8",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                flexWrap: "wrap",
                gap: 2,
                alignItems: { xs: "stretch", md: "center" },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ display: { xs: "none", md: "flex" }, mr: 0.5 }}
              >
                <FilterAltOutlinedIcon sx={{ color: "#7A3FFF", fontSize: 20 }} />
                <Typography variant="body2" fontWeight={600} color="#5a4a7a">
                  Filter by date
                </Typography>
              </Stack>

              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      minWidth: { sm: 170 },
                      flex: { md: "0 1 180px" },
                      bgcolor: "#fff",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    },
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      minWidth: { sm: 170 },
                      flex: { md: "0 1 180px" },
                      bgcolor: "#fff",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={getMuhurt}
                disabled={loading}
                startIcon={<FilterAltOutlinedIcon />}
                sx={{
                  ...gradientBtn,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 120 },
                }}
              >
                {loading ? "Loading..." : "Filter"}
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>

        {/* Table */}
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pb: { xs: 2, sm: 3 },
            pt: 1.5,
            width: "100%",
            overflowX: "auto",
          }}
        >
          <TableCompo
            columns={columns}
            data={notificationList}
            page={page}
            limit={limit}
            totalCount={total || 0}
            onTableChange={handleTableChange}
            loading={loading}
          />
        </Box>
      </Paper>

      <CustomModal
        setOpen={setOpen}
        open={open}
        content={
          <CreateMuhurt
            onSave={(value) => {
              setOpen(value);
              if (!value) {
                getMuhurt();
              }
            }}
            header="Create Muhurt"
            button_name="Create"
          />
        }
      />
      <CustomModal
        setOpen={setUpdate}
        open={update}
        content={
          <CreateMuhurt
            onSave={(value) => {
              setUpdate(value);
              setSelectedRow(null);
              if (!value) {
                getMuhurt();
              }
            }}
            header="Update Muhurt"
            button_name="Update"
            row={selectedRow}
          />
        }
      />
    </Box>
  );
}
